import { useEffect, useRef, type RefObject } from 'react';

/**
 * So viel vom Bild muss bei „cover“ sichtbar bleiben. Passt das Format schlechter ins Fenster – etwa
 * ein Querformat-Video auf einem Handy im Hochformat –, wird das Bild ganz gezeigt. Verzerrt wird nie.
 */
const COVER_MIN_VISIBLE = 0.5;

interface VideoViewProps {
  stream: MediaStream | null;
  muted?: boolean;
  mirrored?: boolean;
  /** Fenster füllen, solange dabei nicht zu viel vom Bild abgeschnitten wird. */
  adaptiveFit?: boolean;
  className?: string;
  videoRef?: RefObject<HTMLVideoElement | null>;
  /** Zähler, der bei einem Gerätewechsel hochgeht. Safari zeigt sonst weiter das alte Bild. */
  revision?: number;
}

export function VideoView({
  stream,
  muted = false,
  mirrored = false,
  adaptiveFit = false,
  className = '',
  videoRef,
  revision = 0,
}: VideoViewProps) {
  const ownRef = useRef<HTMLVideoElement>(null);
  const ref = videoRef ?? ownRef;

  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    // Auch bei gleichem Stream neu setzen: nach einem Gerätewechsel steckt eine neue Spur darin.
    video.srcObject = stream;
    if (stream) {
      video.play().catch(() => {
        // Autoplay blockiert – der Browser startet das Video beim nächsten Klick.
      });
    }
  }, [stream, ref, revision]);

  useEffect(() => {
    const video = ref.current;
    if (!video || !adaptiveFit) return;
    const update = () => {
      const { videoWidth, videoHeight, clientWidth, clientHeight } = video;
      if (!videoWidth || !videoHeight || !clientWidth || !clientHeight) return;
      const videoRatio = videoWidth / videoHeight;
      const boxRatio = clientWidth / clientHeight;
      const visible = Math.min(videoRatio, boxRatio) / Math.max(videoRatio, boxRatio);
      video.style.objectFit = visible >= COVER_MIN_VISIBLE ? 'cover' : 'contain';
    };
    update();
    // „resize“ meldet ein neues Videoformat – z. B. wenn das Gegenüber sein Handy dreht.
    video.addEventListener('loadedmetadata', update);
    video.addEventListener('resize', update);
    const observer = new ResizeObserver(update);
    observer.observe(video);
    return () => {
      video.removeEventListener('loadedmetadata', update);
      video.removeEventListener('resize', update);
      observer.disconnect();
    };
  }, [adaptiveFit, ref]);

  return (
    <video
      ref={ref}
      className={`video${mirrored ? ' video--mirrored' : ''} ${className}`}
      autoPlay
      playsInline
      muted={muted}
    />
  );
}
